import { useEffect, useRef, useState } from 'react';
import { Search, X, ChevronRight, Download } from 'lucide-react';
import type { ActivityFilters, ActivityListItem, ActivityStatus } from '../../types/activity';
import { useActivityFilterOptions, useExportActivities } from '../../hooks/useActivities';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import SkillsGroupedSelect from '../spark-activities/SkillsGroupedSelect';
import PineSelect from '../spark-activities/PineSelect';
import { ACTIVITY_STATUS_DOT, ACTIVITY_STATUS_LABELS } from './activityStatus';

const STATUS_OPTIONS: Array<{ value: ActivityStatus | null; label: string }> = [
  { value: null, label: 'All' },
  { value: 'draft', label: ACTIVITY_STATUS_LABELS.draft },
  { value: 'published', label: ACTIVITY_STATUS_LABELS.published },
  { value: 'unpublished', label: ACTIVITY_STATUS_LABELS.unpublished },
  { value: 'archived', label: ACTIVITY_STATUS_LABELS.archived },
];

interface ActivityListSidebarProps {
  filters: ActivityFilters;
  onUpdateFilter: <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => void;
  onClearFilters: () => void;
  activities: ActivityListItem[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  isLoading: boolean;
  onNewClick: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const idOption = (all: string, items: Array<{ value: number; label: string }>) => [
  { value: '', label: all },
  ...items,
];

export default function ActivityListSidebar({
  filters,
  onUpdateFilter,
  onClearFilters,
  activities,
  selectedId,
  onSelect,
  isLoading,
  onNewClick,
  collapsed = false,
  onToggleCollapse,
}: ActivityListSidebarProps) {
  const { data: filterOptions } = useActivityFilterOptions();
  const exportActivities = useExportActivities();

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchDraft !== filters.search) {
        onUpdateFilter('search', searchDraft);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchDraft, filters.search, onUpdateFilter]);

  const hasFilters = Boolean(
    filters.search ||
      filters.domainId ||
      filters.skillId ||
      filters.playTypeId ||
      filters.physicalDeckId ||
      filters.preferenceId ||
      filters.status,
  );

  const idChange = (key: 'domainId' | 'playTypeId' | 'physicalDeckId' | 'preferenceId') => (v: string | number) =>
    onUpdateFilter(key, v === '' ? null : Number(v));

  if (collapsed) {
    return (
      <aside
        className="border border-pine-line rounded-[14px] shadow-[0_1px_2px_rgba(23,63,56,0.06),0_6px_20px_rgba(23,63,56,0.07)] flex flex-col items-center overflow-hidden bg-pine-card font-body transition-all duration-200 ease-out"
        style={{ width: '60px', flexShrink: 0 }}
      >
        <div className="p-3 border-b border-pine-line w-full flex justify-center">
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md hover:bg-pine-hover-item transition-colors text-pine-ink-soft hover:text-pine-ink"
            title="Expand sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 flex items-start justify-center pt-3">
          <span
            className="text-pine-ink-soft text-[10px] font-semibold"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.08em' }}
          >
            {activities.length} activities
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="activity-list-sidebar"
      className="border border-pine-line rounded-[14px] shadow-[0_1px_2px_rgba(23,63,56,0.06),0_6px_20px_rgba(23,63,56,0.07)] flex flex-col overflow-hidden bg-pine-card font-body transition-all duration-200 ease-out"
      style={{ width: '330px', flexShrink: 0 }}
    >
      <div className="p-3 border-b border-pine-line flex items-center justify-between">
        <span className="text-sm font-semibold text-pine-ink">Activities</span>
        <div className="flex items-center gap-1">
          <button
            onClick={onNewClick}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-pine-pine hover:bg-pine-pine-deep text-white font-semibold rounded-md transition-colors"
          >
            + New
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={exportActivities.isPending}
                data-testid="activity-export-trigger"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-pine-pine hover:bg-pine-pine-deep text-white font-semibold rounded-md transition-colors disabled:opacity-60"
                title="Export all non-archived activities"
              >
                <Download className="w-3.5 h-3.5" />
                {exportActivities.isPending ? 'Exporting…' : 'Export'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => exportActivities.mutate('csv')}>Export CSV</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => exportActivities.mutate('json')}>Export JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md hover:bg-pine-hover-item transition-colors text-pine-ink-soft hover:text-pine-ink"
            title="Collapse sidebar"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-2 border-b border-pine-line">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-pine-ink-soft" />
          <input
            type="text"
            placeholder="Search activities…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-pine-line rounded-md text-pine-ink placeholder-pine-ink-soft focus:outline-none hover:border-pine-pine focus:border-pine-pine"
          />
          {searchDraft && (
            <button
              onClick={() => {
                setSearchDraft('');
                onUpdateFilter('search', '');
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-pine-ink-soft hover:text-pine-ink"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <PineSelect
            options={idOption('All domains', (filterOptions?.domains ?? []).map((d) => ({ value: d.id, label: d.title })))}
            value={filters.domainId ?? ''}
            onChange={idChange('domainId')}
            placeholder="All domains"
          />
          <SkillsGroupedSelect
            skills={filterOptions?.skills.filter((s) => !filters.domainId || s.domainId === filters.domainId) ?? []}
            value={filters.skillId ?? null}
            onChange={(id) => onUpdateFilter('skillId', id)}
          />
          <PineSelect
            options={idOption('All play types', (filterOptions?.playTypes ?? []).map((p) => ({ value: p.id, label: p.title })))}
            value={filters.playTypeId ?? ''}
            onChange={idChange('playTypeId')}
            placeholder="All play types"
          />
          <PineSelect
            options={idOption('All physical decks', (filterOptions?.physicalDecks ?? []).map((d) => ({ value: d.id, label: d.name })))}
            value={filters.physicalDeckId ?? ''}
            onChange={idChange('physicalDeckId')}
            placeholder="All physical decks"
          />
          <div className="col-span-2">
            <PineSelect
              options={idOption('All child preferences', (filterOptions?.preferences ?? []).map((p) => ({ value: p.id, label: p.title })))}
              value={filters.preferenceId ?? ''}
              onChange={idChange('preferenceId')}
              placeholder="All child preferences"
            />
          </div>
        </div>

        <div className="flex gap-1 flex-wrap">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={String(opt.value)}
              onClick={() => onUpdateFilter('status', opt.value)}
              className={`px-2 py-0.5 text-xs rounded-pine-pill border transition-colors ${
                filters.status === opt.value
                  ? 'bg-pine-pine border-pine-pine text-white'
                  : 'border-pine-line text-pine-ink-soft hover:text-pine-ink hover:border-pine-pine'
              }`}
            >
              {opt.label}
            </button>
          ))}
          {hasFilters && (
            <button
              onClick={() => {
                setSearchDraft('');
                onClearFilters();
              }}
              className="px-2 py-0.5 text-xs rounded-pine-pill border border-pine-line text-pine-ink-soft hover:text-pine-ink transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        {isLoading ? (
          <div className="p-4 text-center text-xs text-pine-ink-soft">Loading…</div>
        ) : activities.length === 0 ? (
          <div className="p-4 text-center text-xs text-pine-ink-soft">No activities found</div>
        ) : (
          <div className="flex flex-col gap-[3px] p-1">
            {activities.map((item, index) => (
              <button
                key={item.id}
                data-key={item.key}
                onClick={() => onSelect(item.id)}
                className={`group relative w-full text-left px-[10px] py-[9px] flex items-center gap-2.5 rounded-[10px] hover:bg-pine-hover-item transition-colors ${
                  selectedId === item.id ? 'bg-pine-active-item ring-[1.5px] ring-inset ring-pine-pine' : ''
                }`}
              >
                <span className="w-5 text-right text-xs text-pine-ink-soft flex-shrink-0 tabular-nums">{index + 1}</span>
                <span className="flex-1 text-xs text-pine-ink truncate">{item.title}</span>
                {/* {!item.hasImage && (
                  <span className="text-pine-ink-soft flex-shrink-0" title="No image — app default will be used">
                    <ImageOff className="w-3.5 h-3.5" />
                  </span>
                )} */}
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ACTIVITY_STATUS_DOT[item.status]}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
